import React, { ReactNode, useEffect, useState } from 'react';
import { IoIosArrowDropleft, IoIosArrowDropright } from 'react-icons/io';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  DrawerContent,
  useBreakpointValue,
  useDisclosure,
  Text,
  IconButton,
  Tooltip,
  useBreakpoint,
} from '@chakra-ui/react';
import {
  clearForm,
  useAppDispatch,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { urlFormatter } from 'utils/formaters';
import { Header } from './components/Header';
import { SidebarContent } from './components/SideContent';

interface SideBarProps {
  children: ReactNode;
}

export default function SideBar({ children }: SideBarProps) {
  const dispatch = useAppDispatch();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const breakpoint = useBreakpoint();

  const { pathname } = useLocation();

  if (!pathname.includes('novo-agendamento')) {
    dispatch(clearForm());
  }

  const variant = useBreakpointValue({
    base: isOpen,
    xl: false,
  });

  useEffect(() => {
    if (!variant && isOpen) onClose();
  }, [variant]);

  const {
    user: { token },
  } = useAppSelector(state => state.authSlicer);

  useEffect(() => {
    if (breakpoint !== 'xl') setIsCollapsed(false);
  }, [breakpoint]);

  if (!token) return <Box> {children} </Box>;
  if (pathname === '/login') return <Box> {children} </Box>;

  return (
    <Box minH="100vh" bg="background">
      <SidebarContent
        sideBarName="1"
        onClose={() => onClose}
        isCollapsed={isCollapsed}
        display={{ base: 'none', xl: 'block' }}
      />

      <Drawer
        autoFocus={false}
        isOpen={variant || false}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent
            sideBarName="2"
            isCollapsed={isCollapsed}
            onClose={onClose}
          />
        </DrawerContent>
      </Drawer>

      <Header path={pathname} onOpen={onOpen} />

      <Box
        pt="60px"
        pl={{ base: 0, xl: isCollapsed ? '86px' : '248px' }}
        transition="ease-in-out 0.3s"
      >
        <Box
          zIndex={11}
          h={3}
          w={3}
          position="fixed"
          mt="16px"
          ml="-15px"
          display={{ base: 'none', xl: 'block' }}
        >
          <Tooltip
            bg="ocean.100"
            label={
              isCollapsed ? 'Expandir menu lateral' : 'Recuar menu lateral'
            }
          >
            <IconButton
              aria-label={
                isCollapsed ? 'Expandir menu lateral' : 'Recuar menu lateral'
              }
              icon={
                isCollapsed ? <IoIosArrowDropright /> : <IoIosArrowDropleft />
              }
              size="50px"
              minW={0}
              fontSize="28px"
              onClick={() => setIsCollapsed(prev => !prev)}
              borderRadius="999px"
            />
          </Tooltip>
        </Box>
        <Box
          bg="button"
          borderColor="transparent"
          zIndex={1}
          h={3}
          w={3}
          position="fixed"
          display={{ base: 'none', xl: 'block' }}
        >
          <Box
            bg="background"
            zIndex={2}
            h={3}
            w={3}
            borderStartStartRadius="8px"
            borderColor="transparent"
            position="absolute"
          />
        </Box>
        <Text
          px={{ base: '16px', md: '32px' }}
          pt="20px"
          mb="20px"
          fontSize="xs"
          color="text.200"
        >
          {urlFormatter(pathname)}
        </Text>
        {children}
      </Box>
    </Box>
  );
}
